import { Routes, Route, useParams } from 'react-router-dom'
import { useSession } from '../app/useSession'
import { getQueryParam } from '../lib/query'
import PracticeTracker from '../tools/practice-tracker/PracticeTracker'
import MetronomeV2 from '../tools/metronome-v2/MetronomeV2'
import AudioRecorderV2 from '../tools/audio-recorder-v2/AudioRecorderV2'
import Loading from '../components/Loading'
import ErrorState from '../components/ErrorState'

function ToolContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {children}
      </div>
    </div>
  )
}

function ToolPage() {
  const { slug } = useParams<{ slug: string }>()
  const { session, loading: sessionLoading } = useSession()

  // Parse query params from hash
  const lessonToolId = getQueryParam('lesson_tool_id')

  if (sessionLoading) {
    return (
      <ToolContainer>
        <Loading message="Checking authentication..." />
      </ToolContainer>
    )
  }

  // Route to specific tool based on slug
  switch (slug) {
    case 'practice-tracker':
      if (!session) {
        return (
          <ToolContainer>
            <ErrorState 
              title="Not signed in"
              message="You must be signed in to use this tool. Please sign in through your music studio platform."
            />
          </ToolContainer>
        )
      }
      if (!lessonToolId) {
        return (
          <ToolContainer>
            <ErrorState 
              title="Missing tool configuration"
              message="This tool requires a lesson_tool_id parameter to function properly."
            />
          </ToolContainer>
        )
      }
      return (
        <ToolContainer>
          <PracticeTracker lessonToolId={lessonToolId} />
        </ToolContainer>
      )

    case 'metronome-v2':
      return (
        <ToolContainer>
          <MetronomeV2 />
        </ToolContainer>
      )

    case 'audio-recorder-v2':
      return (
        <ToolContainer>
          <AudioRecorderV2 />
        </ToolContainer>
      )

    default:
      return (
        <ToolContainer>
          <ErrorState 
            title="Tool not found"
            message={`The tool "${slug}" is not available or doesn't exist.`}
          />
        </ToolContainer>
      )
  }
}

export default function ToolRouter() {
  return (
    <Routes>
      <Route path="/:slug" element={<ToolPage />} />
      <Route 
        path="/" 
        element={
          <ToolContainer>
            <ErrorState 
              title="Welcome to MMS Tools Hub"
              message="Please access tools through your music studio platform with the proper tool URL."
            />
          </ToolContainer>
        } 
      />
    </Routes>
  )
}